import { motion, useInView } from "framer-motion";
import { useRef, useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "Como um advogado tributarista me ajuda a lucrar?",
    answer: "Um advogado tributarista identifica oportunidades legais para reduzir sua carga tributária, recuperar impostos pagos indevidamente nos últimos 5 anos e planejar estratégias fiscais que otimizam seu fluxo de caixa. Com a Reforma Tributária de 2026, empresas que se prepararem agora terão vantagem competitiva significativa."
  },
  {
    question: "O que faz um advogado tributarista?",
    answer: "O advogado tributarista é especialista em direito tributário e atua na defesa dos interesses do contribuinte perante o Fisco. Suas principais atividades incluem: planejamento tributário, recuperação de créditos, defesa em autuações fiscais, consultoria sobre enquadramento tributário, análise de benefícios fiscais e acompanhamento de processos administrativos e judiciais relacionados a tributos."
  },
  {
    question: "Reforma tributária em 2026: a advogada tributarista vira a sua maior aliada?",
    answer: "Sim. A Reforma Tributária de 2026 trará mudanças profundas no sistema tributário brasileiro, incluindo a substituição de tributos como PIS, COFINS, IPI, ICMS e ISS. Empresas que não se prepararem adequadamente enfrentarão riscos de pagar mais impostos ou perder créditos. A advogada tributarista é essencial para mapear impactos, adaptar operações e garantir que você aproveite todas as oportunidades da transição."
  },
  {
    question: "Quando devo contratar um escritório de advocacia tributária?",
    answer: "Você deve contratar quando: receber uma autuação fiscal, quiser revisar tributos dos últimos 5 anos, precisar de planejamento tributário, tiver dúvidas sobre enquadramento fiscal, desejar recuperar impostos pagos a mais, ou quiser se preparar para a Reforma Tributária de 2026. Quanto antes agir, maior a economia potencial."
  },
  {
    question: "Como funciona a consultoria jurídica tributária online?",
    answer: "Nosso atendimento é 100% online para todo o Brasil. Após o primeiro contato via WhatsApp, agendamos uma reunião por videoconferência para entender seu caso. Toda a documentação é enviada digitalmente, e você acompanha o andamento do processo em tempo real. Isso garante agilidade e praticidade sem perder a qualidade do serviço presencial."
  },
];

export const HomeFAQ = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  // Inject FAQ Schema JSON-LD
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "home-faq-schema";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    
    // Remove existing schema if present
    const existing = document.getElementById("home-faq-schema");
    if (existing) existing.remove();
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById("home-faq-schema");
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, []);

  return (
    <section id="faq" className="py-12 lg:py-16 bg-background" ref={containerRef}>
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12"
        >
          <span className="text-primary text-sm font-medium tracking-widest uppercase mb-4 block text-center">
            Perguntas Frequentes
          </span>
          <h2 className="font-display text-3xl lg:text-5xl font-light text-foreground text-center">
            Dúvidas sobre <span className="text-primary">Direito Tributário</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto"
        >
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
              >
                <AccordionItem
                  value={`item-${index}`}
                  className="bg-card border border-border rounded-lg px-6 data-[state=open]:border-primary/30"
                >
                  <AccordionTrigger className="text-left font-display text-lg text-foreground hover:text-primary py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
